"use client";

import * as React from "react";
import {Box, Stack, keyframes} from "@mui/material";
import Image from "next/image";

const subtleZoomContinuous = keyframes`
    0% {
        transform: scale(1) translateX(0);
    }

    50% {
        transform: scale(1.06) translateX(-1%);
    }

    100% {
        transform: scale(1) translateX(0);
    }
`;

const dustDrift = keyframes`
    0% {
        transform: translate3d(0, 35px, 0) scale(0.8);
        opacity: 0;
    }

    15% {
        opacity: 0.8;
    }

    75% {
        opacity: 0.55;
    }

    100% {
        transform: translate3d(20px, -130px, 0) scale(1.1);
        opacity: 0;
    }
`;

const dustParticles = [
    {left: "6%", top: "86%", size: 3, duration: 13, delay: 0},
    {left: "12%", top: "64%", size: 2, duration: 16, delay: 3},
    {left: "18%", top: "91%", size: 4, duration: 12, delay: 5},
    {left: "25%", top: "53%", size: 2, duration: 15, delay: 1},
    {left: "32%", top: "79%", size: 3, duration: 14, delay: 4},
    {left: "39%", top: "61%", size: 2, duration: 17, delay: 2},
    {left: "46%", top: "89%", size: 4, duration: 13, delay: 6},
    {left: "53%", top: "48%", size: 2, duration: 16, delay: 1},
    {left: "60%", top: "74%", size: 3, duration: 12, delay: 3},
    {left: "67%", top: "93%", size: 2, duration: 15, delay: 5},
    {left: "74%", top: "57%", size: 4, duration: 14, delay: 0},
    {left: "81%", top: "82%", size: 2, duration: 17, delay: 4},
    {left: "88%", top: "49%", size: 3, duration: 13, delay: 2},
    {left: "94%", top: "88%", size: 3, duration: 16, delay: 6},
];

export default function HeroSection() {
    return (
        <Stack
            position="relative"
            height={{xs: 470, sm: 700, lg: "100%"}}
            minHeight={{xs: 470, sm: 100}}
            overflow="hidden"
            bgcolor="#101522"
        >
            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                width="100%"
                height="100%"
                overflow="hidden"
                sx={{
                    "& img": {
                        objectFit: "cover",
                        objectPosition: "center center",
                        filter: {
                            xs: "brightness(0.88) saturate(1.05) contrast(1.02)",
                            md: "brightness(0.92) saturate(1.12) contrast(1.04)",
                        },
                        willChange: "transform",

                        "@media (max-width:700px)": {
                            objectPosition: "center center",
                            animation: `${subtleZoomContinuous} 16s ease-in-out infinite`,
                        },

                        "@media (min-width:700px)": {
                            objectPosition: "center top",
                            animation: "none",
                        },

                        "@media (min-width:900px)": {
                            objectPosition: "40% 100%",
                        },
                    },
                }}
            >
                <Image
                    src="/images/home/bg.jpg"
                    alt="تصویر انبار"
                    fill
                    priority
                    sizes="100vw"
                />
            </Box>

            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                zIndex={1}
                sx={{
                    pointerEvents: "none",
                    background: `
                        linear-gradient(
                            160deg,
                            rgba(35, 66, 196, 0.12),
                            rgba(13, 23, 71, 0.02) 60%,
                            rgba(13, 23, 71, 0.14)
                        )
                    `,
                    mixBlendMode: "soft-light",
                }}
            />

            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                zIndex={2}
                sx={{
                    pointerEvents: "none",
                    background: `
                        radial-gradient(
                            120% 80% at 50% 12%,
                            rgba(91, 143, 224, 0.05),
                            rgba(13, 23, 71, 0.02) 42%,
                            rgba(5, 7, 15, 0.22) 100%
                        ),
                        linear-gradient(
                            to bottom,
                            rgba(7, 10, 24, 0.12) 0%,
                            rgba(7, 10, 24, 0.04) 45%,
                            rgba(5, 7, 18, 0.22) 100%
                        )
                    `,
                }}
            />

            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                zIndex={3}
                sx={{
                    pointerEvents: "none",
                    boxShadow: {
                        xs: "inset 0 0 80px 10px rgba(3, 5, 12, 0.2)",
                        md: "inset 0 0 150px 25px rgba(3, 5, 12, 0.25)",
                    },
                    background: `
                        radial-gradient(
                            125% 115% at 50% 44%,
                            transparent 58%,
                            rgba(3, 5, 12, 0.18) 100%
                        )
                    `,
                }}
            />

            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                zIndex={4}
                overflow="hidden"
                sx={{
                    pointerEvents: "none",
                    opacity: 0.85,

                    "@media (prefers-reduced-motion: reduce)": {
                        opacity: 0.5,

                        "& > span": {
                            animation: "none",
                        },
                    },
                }}
            >
                {dustParticles.map((particle, index) => (
                    <Box
                        component="span"
                        key={index}
                        sx={{
                            position: "absolute",
                            left: particle.left,
                            top: particle.top,
                            width: particle.size,
                            height: particle.size,
                            borderRadius: "50%",
                            backgroundColor: "rgba(240, 247, 255, 0.9)",
                            boxShadow: `
                                0 0 5px 1px rgba(230, 243, 255, 0.7),
                                0 0 13px 3px rgba(175, 210, 255, 0.35)
                            `,
                            animation: `${dustDrift} ${particle.duration}s linear infinite`,
                            animationDelay: `-${particle.delay}s`,
                            willChange: "transform, opacity",
                        }}
                    />
                ))}
            </Box>
        </Stack>
    );
}